/** Same-origin tab-to-tab push for announcer — instant on Done before the server round trip. */
const CHANNEL = "muone-convocation-live";
export const DISPLAY_COUNTS = [1, 3, 6];

const OPTIMISTIC_STAGE_ACTIONS = new Set([
  "done", "skip", "start_priority", "start_normal", "start_late",
]);

let bc;
function channel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!bc) bc = new BroadcastChannel(CHANNEL);
  return bc;
}

export function postSequenceAction(action) {
  channel()?.postMessage({ type: "sequence_action", action });
}

export function subscribeSequenceAction(handler) {
  const ch = channel();
  if (!ch) return () => {};
  const fn = (evt) => {
    if (evt.data?.type === "sequence_action") handler(evt.data.action);
  };
  ch.addEventListener("message", fn);
  return () => ch.removeEventListener("message", fn);
}

export function isLiveBroadcastAvailable() {
  return typeof BroadcastChannel !== "undefined";
}

export function normalizeDisplayCount(value) {
  const n = Number(value);
  return DISPLAY_COUNTS.includes(n) ? n : 1;
}

export function postLiveCurrent(live, current, currentBatch) {
  const batch = currentBatch ?? (current ? [current] : []);
  channel()?.postMessage({ type: "current", data: { live, current, current_batch: batch } });
}

export function subscribeLiveCurrent(handler) {
  const ch = channel();
  if (!ch) return () => {};
  const fn = (evt) => {
    if (evt.data?.type === "current") handler(evt.data.data);
  };
  ch.addEventListener("message", fn);
  return () => ch.removeEventListener("message", fn);
}

/** Announcer responds when opened in another tab on the same machine/browser. */
export function subscribeLocalAnnouncerPing() {
  const ch = channel();
  if (!ch) return () => {};
  const fn = (evt) => {
    if (evt.data?.type === "ping") ch.postMessage({ type: "pong" });
  };
  ch.addEventListener("message", fn);
  return () => ch.removeEventListener("message", fn);
}

export function probeLocalAnnouncer(onResult, timeoutMs = 800) {
  const ch = channel();
  if (!ch) {
    onResult(false);
    return () => {};
  }
  let settled = false;
  const finish = (ok) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    ch.removeEventListener("message", onPong);
    onResult(ok);
  };
  const onPong = (evt) => {
    if (evt.data?.type === "pong") finish(true);
  };
  ch.addEventListener("message", onPong);
  ch.postMessage({ type: "ping" });
  const timer = setTimeout(() => finish(false), timeoutMs);
  return () => finish(false);
}

/** Mirror server _cap_to_same_category — batches must not span student categories. */
export function capToSameCategory(items, count, anchorCategoryId = undefined) {
  if (!items?.length || count <= 0) return [];
  const anchor = anchorCategoryId !== undefined ? anchorCategoryId : items[0]?.student_category_id;
  const out = [];
  for (const item of items) {
    if (out.length >= count) break;
    if (item?.student_category_id !== anchor) break;
    out.push(item);
  }
  return out;
}

function queueKeyForType(qtype) {
  if (qtype === "PRIORITY") return "priority";
  if (qtype === "LATE") return "late";
  return "normal";
}

/** Advance by the configured on-stage batch size using backstage state. */
export function advanceDoneLocally(state) {
  if (!state) return null;
  const live = state.live || {};
  const displayCount = normalizeDisplayCount(live.display_count);
  const qtype = live.queue_type || "PRIORITY";
  const queueKey = queueKeyForType(qtype);
  const queue = state[queueKey] || [];
  const nextBatch = capToSameCategory(queue, displayCount).map((x) => ({ ...x, status: "CURRENT" }));
  const current = nextBatch[0] || null;
  const newLive = {
    ...live,
    current_registration_id: current?.registration_id ?? null,
    is_paused: false,
    queue_type: qtype,
    display_count: displayCount,
  };
  return {
    ...state,
    live: newLive,
    current,
    current_batch: nextBatch,
    [queueKey]: queue.slice(nextBatch.length),
  };
}

/** Optimistic first batch when starting a queue (same machine announcer). */
export function advanceStartLocally(state, qtype) {
  if (!state) return null;
  const live = state.live || {};
  const displayCount = normalizeDisplayCount(live.display_count);
  const queueKey = queueKeyForType(qtype);
  const queue = state[queueKey] || [];
  const batch = capToSameCategory(queue, displayCount).map((x) => ({ ...x, status: "CURRENT" }));
  const current = batch[0] || null;
  const newLive = {
    ...live,
    current_registration_id: current?.registration_id ?? null,
    is_paused: false,
    queue_type: qtype,
    display_count: displayCount,
  };
  return {
    ...state,
    live: newLive,
    current,
    current_batch: batch,
    [queueKey]: queue.slice(batch.length),
  };
}

export function applyStageOptimistic(state, action) {
  if (!state || !OPTIMISTIC_STAGE_ACTIONS.has(action)) return null;
  if (action === "done" || action === "skip") return advanceDoneLocally(state);
  if (action.startsWith("start_")) {
    const qtype = action.replace("start_", "").toUpperCase();
    return advanceStartLocally(state, qtype);
  }
  return null;
}

export function mergeLiveCurrent(prev, data) {
  const displayCount = normalizeDisplayCount(data.live?.display_count ?? prev?.live?.display_count);
  const raw = data.current_batch ?? (data.current ? [data.current] : []);
  const batch = raw.slice(0, displayCount);
  return {
    ...(prev || {}),
    live: data.live,
    current: data.current ?? batch[0] ?? null,
    current_batch: batch,
  };
}
